import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CartService } from '../services/cartService';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('quikr_access_token');
    const emailVerified = localStorage.getItem('quikr_email_verified') === 'true';
    return Boolean(token && emailVerified);
  }, []);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated()) {
      setCartItems([]);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await CartService.getCartContent();
    
    if (result.success) {
      setCartItems(result.items);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  }, [isAuthenticated]);


  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const addToCart = useCallback(async (uuid) => {
    if (!isAuthenticated()) {
      return { success: false, message: 'Vous devez être connecté pour ajouter au panier' };
    }

    setLoading(true);
    const result = await CartService.addToCart(uuid);
    
    if (result.success) {
      await loadCart();
    }
    
    setLoading(false);
    return result;
  }, [isAuthenticated, loadCart]);

  const removeFromCart = useCallback(async (uuid) => {
    if (!isAuthenticated()) {
      return { success: false, message: 'Vous devez être connecté' };
    }

    setLoading(true);
    const result = await CartService.removeFromCart(uuid);
    
    if (result.success) {
      setCartItems(prev => prev.filter(item => item.uuid !== uuid));
    }
    
    setLoading(false);
    return result;
  }, [isAuthenticated]);

  const isInCart = useCallback((uuid) => {
    return cartItems.some(item => item.uuid === uuid);
  }, [cartItems]);

  const proceedToPayment = useCallback(async () => {
    if (!isAuthenticated()) {
      return { success: false, message: 'Vous devez être connecté pour payer' };
    }

    if (cartItems.length === 0) {
      return { success: false, message: 'Votre panier est vide' };
    }

    setLoading(true);
    const result = await CartService.getPaymentPageUrl();
    
    if (result.success && result.url) {
      window.location.href = result.url;
      return { success: true };
    }
    
    setLoading(false);
    return result;
  }, [isAuthenticated, cartItems.length]);

  const total = CartService.calculateTotal(cartItems);
  const itemCount = cartItems.length;

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const value = {
    cartItems,
    loading,
    error,
    itemCount,
    total,
    addToCart,
    removeFromCart,
    isInCart,
    loadCart,
    proceedToPayment,
    clearCart,
    isAuthenticated,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
